"use client";

/**
 * @module Carousel
 *
 * Touch-enabled carousel/slider built on Embla Carousel.
 * Supports horizontal/vertical orientation, keyboard navigation, and custom plugins.
 *
 * @see {@link https://ui.shadcn.com/docs/components/carousel Shadcn Carousel}
 * @see {@link https://www.embla-carousel.com/ Embla Carousel}
 *
 * @example
 * // Basic carousel
 * <Carousel>
 *   <CarouselContent>
 *     <CarouselItem>Slide 1</CarouselItem>
 *     <CarouselItem>Slide 2</CarouselItem>
 *     <CarouselItem>Slide 3</CarouselItem>
 *   </CarouselContent>
 *   <CarouselPrevious />
 *   <CarouselNext />
 * </Carousel>
 *
 * @example
 * // With API access and autoplay
 * const [api, setApi] = useState<CarouselApi>();
 *
 * <Carousel setApi={setApi} opts={{ loop: true }}>
 *   <CarouselContent>...</CarouselContent>
 * </Carousel>
 */
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type * as React from "react";
import type { Ref } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { Button } from "./button";

/** Embla Carousel API type for external control */
type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];
interface CarouselProps {
  opts?: CarouselOptions;
  orientation?: "horizontal" | "vertical";
  plugins?: CarouselPlugin;
  setApi?: (api: CarouselApi) => void;
}
type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;
const CarouselContext = createContext<CarouselContextProps | null>(null);

/**
 * Hook to access carousel state and controls from child components.
 * Must be used within a Carousel component.
 *
 * @returns Carousel API, scroll functions, and navigation state
 * @throws Error if used outside of Carousel
 */
export function useCarousel() {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}

/**
 * Root carousel container. Provides context for child components.
 *
 * @param opts - Embla Carousel options (loop, align, dragFree, etc.)
 * @param plugins - Embla plugins (autoplay, etc.)
 * @param orientation - `"horizontal"` (default) or `"vertical"`
 * @param setApi - Callback to receive the carousel API
 */
const Carousel = ({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & CarouselProps & {
  ref?: Ref<HTMLDivElement>;
}) => {
  const [carouselRef, api] = useEmblaCarousel({
    ...opts,
    axis: orientation === "horizontal" ? "x" : "y"
  }, plugins);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const onSelect = useCallback((carouselApi: CarouselApi) => {
    if (!carouselApi) {
      return;
    }
    setCanScrollPrev(carouselApi.canScrollPrev());
    setCanScrollNext(carouselApi.canScrollNext());
  }, []);
  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      scrollPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      scrollNext();
    }
  }, [scrollPrev, scrollNext]);
  const carouselContextValue = useMemo(() => ({
    carouselRef,
    api,
    opts,
    orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext
  }), [api, canScrollNext, canScrollPrev, carouselRef, opts, orientation, scrollNext, scrollPrev]);
  useEffect(() => {
    if (!(api && setApi)) {
      return;
    }
    setApi(api);
  }, [api, setApi]);
  useEffect(() => {
    if (!api) {
      return;
    }
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);
    return () => {
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);
  return <CarouselContext.Provider value={carouselContextValue} data-sentry-element="CarouselContext.Provider" data-sentry-component="Carousel" data-sentry-source-file="carousel.tsx">
      <div aria-roledescription="carousel" className={cn("relative", className)} onKeyDownCapture={handleKeyDown} ref={ref} role="region" {...props}>
        {children}
      </div>
    </CarouselContext.Provider>;
};

/**
 * Container for carousel slides. Handles overflow and flex layout.
 * @param containerClassName - Classes for the outer overflow container
 */
const CarouselContent = ({
  className,
  containerClassName,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  containerClassName?: string;
  ref?: Ref<HTMLDivElement>;
}) => {
  const {
    carouselRef,
    orientation
  } = useCarousel();
  return <div className={cn("overflow-hidden", containerClassName)} ref={carouselRef} data-sentry-component="CarouselContent" data-sentry-source-file="carousel.tsx">
      <div className={cn("flex", orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col", className)} ref={ref} {...props} />
    </div>;
};

/** Props for CarouselItem component */
interface CarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
  useCustomStyles?: boolean;
}

/**
 * Individual slide within the carousel.
 * @param useCustomStyles - Use responsive grid-based sizing instead of full-width
 */
const CarouselItem = ({
  className,
  useCustomStyles,
  ref,
  ...props
}: CarouselItemProps) => {
  const {
    orientation
  } = useCarousel();
  return <div aria-roledescription="slide" className={cn(useCustomStyles ? "min-w-0 shrink-0 grow-0 @[1150px]:basis-1/6 @[1400px]:basis-[14.28%] @[1580px]:basis-[12.5%] @[540px]:basis-1/4 @[900px]:basis-1/5 basis-1/3 overflow-hidden px-0 pr-2" : cn("min-w-0 shrink-0 grow-0 basis-full", orientation === "horizontal" ? "pl-4" : "pt-4"), className)} ref={ref} role="group" {...props} data-sentry-component="CarouselItem" data-sentry-source-file="carousel.tsx" />;
};

/** Previous slide button. Auto-disabled when at the start. */
const CarouselPrevious = ({
  className,
  variant = "subtle",
  size = "icon",
  ref,
  ...props
}: React.ComponentProps<typeof Button> & {
  ref?: Ref<HTMLButtonElement>;
}) => {
  const {
    orientation,
    scrollPrev,
    canScrollPrev
  } = useCarousel();
  return <Button className={cn("absolute h-8 w-8 rounded-full", orientation === "horizontal" ? "top-1/2 -left-12 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90", !canScrollPrev && "cursor-default!", className)} disabled={!canScrollPrev} onClick={scrollPrev} ref={ref} size={size} variant={variant} {...props} data-sentry-element="Button" data-sentry-component="CarouselPrevious" data-sentry-source-file="carousel.tsx">
      <ArrowLeft className="h-4 w-4" data-sentry-element="ArrowLeft" data-sentry-source-file="carousel.tsx" />
      <span className="sr-only">Previous slide</span>
    </Button>;
};

/** Next slide button. Auto-disabled when at the end. */
const CarouselNext = ({
  className,
  variant = "subtle",
  size = "icon",
  ref,
  ...props
}: React.ComponentProps<typeof Button> & {
  ref?: Ref<HTMLButtonElement>;
}) => {
  const {
    orientation,
    scrollNext,
    canScrollNext
  } = useCarousel();
  return <Button className={cn("absolute h-8 w-8 rounded-full", orientation === "horizontal" ? "top-1/2 -right-12 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90", !canScrollNext && "cursor-default!", className)} disabled={!canScrollNext} onClick={scrollNext} ref={ref} size={size} variant={variant} {...props} data-sentry-element="Button" data-sentry-component="CarouselNext" data-sentry-source-file="carousel.tsx">
      <ArrowRight className="h-4 w-4" data-sentry-element="ArrowRight" data-sentry-source-file="carousel.tsx" />
      <span className="sr-only">Next slide</span>
    </Button>;
};
export { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselProps };